/**
 * profile-password.js
 */

import React from 'react';
import axios from 'axios';
import { Grid } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { AuthContext } from 'contexts/auth/auth-context';
import Button from 'components/button';
import ChangePasswordFields from 'components/change-password-fields';
import ConfirmDialog from 'components/confirm-dialog';
import ConfirmUnsavedEditor from 'components/menu-drawer/confirm-unsaved-editor';
import DrawerContentBuilder from 'components/menu-drawer/drawer-content-builder';
import DrawerActionButton from 'components/menu-drawer/drawer-action-button';
import DrawerContent from 'components/menu-drawer/drawer-content';
import HelpTextItem from 'components/menu-drawer/help-text-item';
import { EditorIconSection } from 'components/icon-section/icon-section';
import MobileDrawerAppBar from 'components/menu-drawer/mobile/mobile-drawer-app-bar';


export default function UserChangePassword({ drawerState, onClose }) {
  const formContext = useForm({ defaultValues: { newPassword: '', confirmPassword: '' } });
  const { handleSubmit, formState } = formContext;
  const { enqueueSnackbar } = useSnackbar();
  const { logout } = React.useContext(AuthContext);
  const [openSuccessDialog, setOpenSuccessDialog] = React.useState(false);
  const [confirmPwdChanged, setConfirmPwdChanged] = React.useState();
  const { t } = useTranslation('profileEdit');
  const title = "Change Password";


  React.useEffect(() => {
    const logoutUser = async () => {
      await confirmPwdChanged();
      logout();
    }

    if (confirmPwdChanged) {
      logoutUser();
    }
  }, [confirmPwdChanged, logout]);


  const onSubmit = async (formData) => {
    drawerState.loading.content.set(true);
    try {
      await axios.post('/account/password', formData, { bypassErrorHandling: true });
      drawerState.loading.content.set(false);
      setOpenSuccessDialog(true);
    } catch (error) {
      drawerState.loading.content.set(false);
      enqueueSnackbar('Failed to reset your password', {
        variant: 'error',
        anchorOrigin: {
          vertical: "top",
          horizontal: "center"
        }
      });
    }
  }


  const cancelButton = (
    <Button upperCase={false} onClick={onClose}>Cancel</Button>
  );


  const saveButton = (
    <DrawerActionButton
      level={1}
      disabled={!formState.isDirty}
      style={{ marginLeft: 16 }}
      type="submit"
    >
      Save
    </DrawerActionButton>
  );


  return (
    <>
      <FormProvider {...formContext}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
          <DrawerContentBuilder
            mobileAppBar={{ content: (
              <MobileDrawerAppBar
                title={title}
                saveButton={saveButton}
                onClose={onClose}
              />
            )}}
            desktopHeader={{
              title,
              actions: <>{cancelButton}{saveButton}</>
            }}
            children={(
              <DrawerContent>
                <EditorIconSection
                  iconName="password"
                  helpProps={{
                    translation: 'profileEdit',
                    section: 'passwordReset'
                  }}
                >
                  <HelpTextItem
                    sidebarText={drawerState.sidebarText}
                    title={t('passwordReset.helpTitle')}
                    text={t('passwordReset.helpInfo')}
                  >
                    <Grid container spacing={2} direction="column">
                      <ChangePasswordFields />
                    </Grid>
                  </HelpTextItem>
                </EditorIconSection>
              </DrawerContent>
            )}
          />
        </form>
      </FormProvider>

      <ConfirmUnsavedEditor
        dirty={formState.isDirty}
        save={async () => handleSubmit(onSubmit)()}
      />

      {openSuccessDialog &&
        <ConfirmDialog
          content={t("passwordReset.successMessage")}
          title='Password Change Successful'
          titleIcon='info'
          setConfirm={setConfirmPwdChanged}
          customActions={[
            {
              label: 'OK',
              value: true,
            },
          ]}
        />}
    </>
  )
}